/**
 * GDELT Doc 2.0 — media-coverage adapter for Phase 5.
 *
 * GDELT is free, no-auth, comprehensive global news indexing. Rate-limit is
 * 1 request per 5 seconds (enforced server-side; HTTP 429 on violation).
 * Docs: https://blog.gdeltproject.org/announcing-the-gdelt-doc-2-0-api/
 *
 * Query shape:
 *   https://api.gdeltproject.org/api/v2/doc/doc
 *     ?query=<keywords>          quoted phrases work; AND is implicit
 *     &mode=ArtList              return article list (JSON)
 *     &format=json
 *     &maxrecords=N              max 250 per call
 *     &timespan=Nd               last N days (or use startdatetime/enddatetime)
 *
 * Response.articles[]:
 *   { url, title, seendate (YYYYMMDDTHHMMSSZ), domain, language, sourcecountry }
 *
 * Sentiment + outbreak-mapping confidence are NOT in the GDELT response;
 * we derive them locally — see `classifyArticle` below.
 */

import type {
  CoverageArticle, CoverageLevel, CredibilityTier, Sentiment, ArticleType,
  MappingConfidence,
} from './types';

export interface GdeltRawArticle {
  url: string;
  url_mobile?: string;
  title: string;
  seendate: string;          // YYYYMMDDTHHMMSSZ
  socialimage?: string;
  domain: string;
  language?: string;
  sourcecountry?: string;
}

export interface GdeltDocResponse {
  articles?: GdeltRawArticle[];
}

/** Build a GDELT Doc 2.0 query URL.
 *
 *  Per https://blog.gdeltproject.org/gdelt-doc-2-0-api-debuts/: the engine
 *  AND's terms, supports quoted phrases, and accepts narrow operators like
 *  `sourcecountry:XX` (FIPS country code). We keep queries phrase-anchored
 *  + country-narrowed to keep the article→outbreak mapping high-confidence. */
export function buildGdeltUrl(opts: {
  keywords: string;          // e.g. '"ebola" "congo"' — caller responsible for escaping
  timespan?: '7d' | '30d' | '90d';
  maxRecords?: number;       // 1..250
}): string {
  const ts = opts.timespan ?? '7d';
  const max = Math.min(250, Math.max(1, opts.maxRecords ?? 50));
  const q = encodeURIComponent(opts.keywords);
  return `https://api.gdeltproject.org/api/v2/doc/doc?query=${q}&mode=ArtList&format=json&maxrecords=${max}&timespan=${ts}`;
}

// ---------------------------------------------------------------------------
// Credibility classification — small curated allowlist. Domains in T1 are
// official health agencies + WHO bodies; T2 is major mainstream news outlets;
// everything else falls through to T3 (aggregator). The allowlist is
// deliberately conservative — better to under-classify a real T1 source than
// over-classify (which would inflate the "official coverage" rating).
// ---------------------------------------------------------------------------

/** Domain suffixes (or exact matches) for Tier 1 — official health agencies +
 *  multilateral health bodies. Suffix match: `who.int` matches `apps.who.int`. */
const T1_DOMAINS: readonly string[] = [
  'who.int',
  'cdc.gov',
  'ecdc.europa.eu',
  'paho.org',
  'africacdc.org',
  'emro.who.int',
  'searo.who.int',
  'wpro.who.int',
  'euro.who.int',
  'afro.who.int',
  'fic.nih.gov',
  'nicd.ac.za',
  'ncdc.gov.ng',
  'health.gov.au',
  'health.gov.uk',
  'nhs.uk',
  'gov.uk',                   // catches DHSC, UKHSA
  'rki.de',
  'sciensano.be',
  'rivm.nl',
  'folkhalsomyndigheten.se',
  'thl.fi',
  'ssi.dk',
  'bag.admin.ch',
  'phw.nhs.wales',
  'publichealthscotland.scot',
  'gov.ie',                   // catches HSE
  'salud.gob.mx',
  'salud.gob.ar',
  'saude.gov.br',
  'ministeriodesalud.go.cr',
  'msp.gob.pa',
  'msal.gob.ar',
  'gob.mx',
  'mhlw.go.jp',
  'cdc.gov.tw',
  'chp.gov.hk',
  'moh.gov.sg',
  'moh.gov.my',
  'moh.gov.in',
  'mohfw.gov.in',
  'ddc.moph.go.th',
  'dghs.gov.bd',
  'nih.org.pk',
  'epid.gov.lk',
  'health.gov.za',
  'health.gov.ng',
  'promedmail.org',
];

/** Domain suffixes for Tier 2 — mainstream news outlets with editorial
 *  oversight. Same suffix-match semantics as T1. */
const T2_DOMAINS: readonly string[] = [
  'reuters.com', 'apnews.com',
  'bbc.com', 'bbc.co.uk',
  'theguardian.com',
  'nytimes.com', 'washingtonpost.com', 'wsj.com',
  'ft.com', 'bloomberg.com',
  'cnn.com', 'nbcnews.com', 'cbsnews.com', 'abcnews.go.com',
  'aljazeera.com',
  'afp.com', 'france24.com', 'dw.com',
  'scmp.com', 'japantimes.co.jp', 'asahi.com', 'mainichi.jp',
  'theaustralian.com.au', 'smh.com.au', 'abc.net.au',
  'sciencemag.org', 'science.org', 'nature.com',
  'statnews.com', 'cidrap.umn.edu',
  'lancet.com', 'thelancet.com', 'nejm.org',
  'lemonde.fr', 'spiegel.de', 'zeit.de',
  'foxnews.com', 'usatoday.com', 'npr.org',
  'cbc.ca', 'reuters.co.uk',
];

/** Classify a domain into a credibility tier. Suffix-match against the
 *  allowlists; default to T3. The domain field from GDELT may include
 *  subdomain (e.g. `apps.who.int`) — suffix match handles that. */
export function classifyCredibility(domain: string): CredibilityTier {
  const d = domain.toLowerCase();
  for (const t1 of T1_DOMAINS) if (d === t1 || d.endsWith('.' + t1)) return 1;
  for (const t2 of T2_DOMAINS) if (d === t2 || d.endsWith('.' + t2)) return 2;
  return 3;
}

/** Map a credibility tier → article-type taxonomy (per the CD spec UI which
 *  filters by type, not tier). T1 → official, T2 → mainstream, T3 → aggregator. */
export function tierToType(cred: CredibilityTier): ArticleType {
  return cred === 1 ? 'official' : cred === 2 ? 'mainstream' : 'aggregator';
}

// ---------------------------------------------------------------------------
// Sentiment classification — keyword heuristic. GDELT's own TONE field IS
// available via the Doc 2.0 GKG endpoint but adds a second API call per
// article; the headline-keyword approach is faster + good-enough for the
// CD spec's three-bucket scale (measured / neutral / alarmist).
// ---------------------------------------------------------------------------

const ALARMIST_PATTERNS = [
  /\b(deadly|terrifying|catastroph|crisis|rampant|spreading|spreads|grips|ravag|disaster|nightmare|threat|spiraling|out of control|panic|fears?(?!\s+over\s+contained))\b/i,
  /\b(killer|dread|grim|grave|alarming|escalat|raging)\b/i,
];

const MEASURED_PATTERNS = [
  /\b(situation report|sitrep|update|surveillance|response|deploy|investigation|cluster|notification|reported|confirms?|cases? identified|laboratory[- ]confirmed)\b/i,
  /\b(ministry|health authorities|cdc|who|paho|epidemiolog)\b/i,
];

/** Classify article sentiment from headline. NEVER a judgement about the
 *  outbreak — just the article's TONE. An alarmist aggregator and a measured
 *  WHO report can describe the same event; both visible, operator decides. */
export function classifySentiment(headline: string): Sentiment {
  // Score-based to handle headlines hitting both pattern sets (alarmist wins
  // the tie — operators prefer to know the tone-overload risk).
  let alarmist = 0;
  let measured = 0;
  for (const re of ALARMIST_PATTERNS) if (re.test(headline)) alarmist++;
  for (const re of MEASURED_PATTERNS) if (re.test(headline)) measured++;
  if (alarmist > 0 && alarmist >= measured) return 'alarmist';
  if (measured > 0) return 'measured';
  return 'neutral';
}

// ---------------------------------------------------------------------------
// Outbreak-mapping confidence — `high` when BOTH the pathogen keyword AND
// the country/region keyword appear in the headline; `ambiguous` when only
// the pathogen keyword does (the country narrowing was just the GDELT query
// filter, so we can't be sure the article ITSELF mentions the country —
// e.g. a global "diseases tracker" round-up). Surfacing the distinction is
// the CD spec's honesty rail.
// ---------------------------------------------------------------------------

/** Map mapping confidence from a headline + pathogen + country tokens. */
export function classifyMapping(
  headline: string,
  pathogenTokens: readonly string[],
  countryTokens: readonly string[],
): MappingConfidence {
  const h = headline.toLowerCase();
  const pathHit = pathogenTokens.some(t => h.includes(t.toLowerCase()));
  const countryHit = countryTokens.some(t => h.includes(t.toLowerCase()));
  return pathHit && countryHit ? 'high' : 'ambiguous';
}

// ---------------------------------------------------------------------------
// Coverage volume — ordinal (silent / low / medium / high) from a raw count.
// Thresholds match the sample-data narrative: <5 = silent (very few items),
// 5..15 = low, 15..50 = medium, 50+ = high. Calibrate against real data
// once we have enough corpus to know what "normal" looks like per outbreak.
// ---------------------------------------------------------------------------

export function classifyCoverageLevel(count: number): CoverageLevel {
  if (count < 5) return 0;
  if (count < 15) return 1;
  if (count < 50) return 2;
  return 3;
}

// ---------------------------------------------------------------------------
// Main transform — raw GDELT response → CoverageArticle[]. Pure; testable
// offline. The fetch wrapper is in scripts/ingest-coverage.ts.
// ---------------------------------------------------------------------------

export function gdeltToArticles(
  raw: GdeltDocResponse,
  opts: {
    outbreakId: string;
    pathogenTokens: readonly string[];
    countryTokens: readonly string[];
    /** Cap on articles taken from this response (caller-side dedup happens
     *  later if multiple GDELT queries fed the same outbreak). */
    take?: number;
  },
): CoverageArticle[] {
  const out: CoverageArticle[] = [];
  const articles = raw.articles ?? [];
  for (let i = 0; i < articles.length && i < (opts.take ?? articles.length); i++) {
    const a = articles[i]!;
    if (!a.url || !a.title || !a.seendate) continue;
    const cred = classifyCredibility(a.domain);
    out.push({
      id: `gdelt-${hashStable(a.url)}`,
      outbreakId: opts.outbreakId,
      headline: a.title,
      source: a.domain,
      type: tierToType(cred),
      cred,
      date: gdeltDateToIso(a.seendate),
      sentiment: classifySentiment(a.title),
      conf: classifyMapping(a.title, opts.pathogenTokens, opts.countryTokens),
      url: a.url,
    });
  }
  return out;
}

/** GDELT seendate (YYYYMMDDTHHMMSSZ) → ISO 8601 with separators. */
function gdeltDateToIso(d: string): string {
  // Accept malformed dates by returning the raw if it doesn't match.
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(d);
  if (!m) return d;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
}

/** Stable short-hash for the URL → article id. Not cryptographic; collision-
 *  resistant enough for a few hundred articles per ingest. */
function hashStable(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}
